import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, CircleUserRound, Clapperboard, CreditCard, GraduationCap, MessageSquareText, Mic2, Presentation, ReceiptText, Route } from 'lucide-react';
import SiteBrandedLoader from '../components/SiteBrandedLoader';
import { DashboardHero } from '../components/dashboard/DashboardHero';
import { DashboardTabItem, DashboardTabs } from '../components/dashboard/DashboardTabs';
import { DashboardSidebar, DashboardSidebarItem } from '../components/dashboard/DashboardSidebar';
import {
  DashboardAudiosPanel,
  DashboardCoursesPanel,
  DashboardMessagesPanel,
  DashboardOrdersPanel,
  DashboardVideosPanel,
  DashboardWalletPanel,
  DashboardWorkshopsPanel,
} from '../components/dashboard/DashboardPanels';
import { DashboardGrowthPanel } from '../components/dashboard/DashboardGrowthPanel';
import MyAssessmentHistory from '../components/dashboard/MyAssessmentHistory';
import { DashboardPortalPanel } from '../components/dashboard/DashboardPortalPanel';
import { DashboardCard, DashboardSectionTitle } from '../components/dashboard/DashboardShared';
import { useAuth } from '../contexts/AuthContext';
import { AssessmentResult, Audio, Course, UserMessage, Video, Workshop } from '../types';
import { PortalSummary } from '../types/portal';
import { portalService, assessmentsService, authService, audiosService, coursesService, messagesService, paymentsService, videosService, workshopsService } from '../services/api';

type DashboardTabKey = 'courses' | 'growth' | 'portal' | 'workshops' | 'videos' | 'audios' | 'wallet' | 'orders' | 'messages' | 'profile';

const tabs: DashboardTabItem[] = [
  { key: 'courses', label: 'دوره‌ها' },
  { key: 'growth', label: 'مسیر رشد من' },
  { key: 'portal', label: 'پرتال کوچینگ' },
  { key: 'workshops', label: 'کارگاه‌ها' },
  { key: 'videos', label: 'ویدیوها' },
  { key: 'audios', label: 'صوت‌ها' },
  { key: 'wallet', label: 'کیف پول' },
  { key: 'orders', label: 'سفارش‌های من' },
  { key: 'messages', label: 'پیام‌ها' },
  { key: 'profile', label: 'پروفایل' },
];

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, updateProfile: saveProfile } = useAuth();

  const [activeTab, setActiveTab] = useState<DashboardTabKey>('courses');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [myCourses, setMyCourses] = useState<Course[]>([]);
  const [myWorkshops, setMyWorkshops] = useState<Workshop[]>([]);
  const [myVideos, setMyVideos] = useState<Video[]>([]);
  const [myAudios, setMyAudios] = useState<Audio[]>([]);
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [growthResult, setGrowthResult] = useState<AssessmentResult | null>(null);
  const [growthLocked, setGrowthLocked] = useState(false);
  const [growthAllowRetake, setGrowthAllowRetake] = useState(true);
  const [portalSummary, setPortalSummary] = useState<PortalSummary | null>(null);

  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    education: '',
    university: '',
    job: '',
    state: '',
    gender: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileFeedback, setProfileFeedback] = useState<{ success?: string; error?: string }>({});

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState<{ success?: string; error?: string }>({});

  const unreadMessages = useMemo(() => messages.filter((m) => !m.isRead).length, [messages]);
  const sidebarItems: DashboardSidebarItem[] = useMemo(
    () => [
      { key: 'courses', label: 'دوره‌ها', icon: <BookOpen className="h-4 w-4" />, badge: myCourses.length ? myCourses.length.toLocaleString('fa-IR') : '' },
      ...(portalSummary
        ? [
            {
              key: 'portal',
              label: 'پرتال کوچینگ',
              icon: <GraduationCap className="h-4 w-4" />,
              /* فقط کار فوری بج می‌گیرد؛ عدد بی‌معنی روی منو، بج را بی‌اثر می‌کند */
              badge: portalSummary.urgent ? portalSummary.urgent.toLocaleString('fa-IR') : '',
            },
          ]
        : []),
      { key: 'growth', label: 'مسیر رشد من', icon: <Route className="h-4 w-4" />, badge: growthResult ? growthResult.summary.highPriorityCount.toLocaleString('fa-IR') : '' },
      { key: 'workshops', label: 'کارگاه‌ها', icon: <Presentation className="h-4 w-4" />, badge: myWorkshops.length ? myWorkshops.length.toLocaleString('fa-IR') : '' },
      { key: 'videos', label: 'ویدیوها', icon: <Clapperboard className="h-4 w-4" />, badge: myVideos.length ? myVideos.length.toLocaleString('fa-IR') : '' },
      { key: 'audios', label: 'صوت‌ها', icon: <Mic2 className="h-4 w-4" />, badge: myAudios.length ? myAudios.length.toLocaleString('fa-IR') : '' },
      { key: 'wallet', label: 'کیف پول', icon: <CreditCard className="h-4 w-4" />, badge: `${walletBalance.toLocaleString('fa-IR')}` },
      { key: 'orders', label: 'سفارش‌های من', icon: <ReceiptText className="h-4 w-4" />, badge: invoices.length ? invoices.length.toLocaleString('fa-IR') : '' },
      { key: 'messages', label: 'پیام‌ها', icon: <MessageSquareText className="h-4 w-4" />, badge: unreadMessages ? unreadMessages.toLocaleString('fa-IR') : '' },
      { key: 'profile', label: 'پروفایل', icon: <CircleUserRound className="h-4 w-4" /> },
    ],
    [myCourses.length, growthResult, portalSummary, myWorkshops.length, myVideos.length, myAudios.length, walletBalance, invoices.length, unreadMessages],
  );

  const bootstrapProfileForm = useCallback(() => {
    if (!user) return;
    setProfileForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      education: user.education || '',
      university: user.university || '',
      job: user.job || '',
      state: user.state || '',
      gender: user.gender || '',
    });
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [coursesData, workshopsData, videosData, audiosData, messagesData, invoicesData, walletData, growthData, assessmentLanding, portalData] = await Promise.all([
        coursesService.getMyCourses(),
        workshopsService.getMyWorkshops(),
        videosService.getMyVideos(),
        audiosService.getMyAudios(),
        messagesService.getMyMessages(),
        paymentsService.getMyInvoices(15),
        paymentsService.getWalletBalance(),
        // نبودِ پرسشنامهٔ فعال نباید کل داشبورد را خطا کند
        assessmentsService.getMyResult().catch(() => null),
        assessmentsService.getLanding().catch(() => null),
        // نبودن در دورهٔ کوچینگ خطا نیست؛ فقط تب نمایش داده نمی‌شود
        portalService.getSummary().catch(() => null),
      ]);
      setMyCourses(Array.isArray(coursesData) ? coursesData : []);
      setMyWorkshops(Array.isArray(workshopsData) ? workshopsData : []);
      setMyVideos(Array.isArray(videosData) ? videosData : []);
      setMyAudios(Array.isArray(audiosData) ? audiosData : []);
      setMessages(Array.isArray(messagesData) ? messagesData : []);
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
      setWalletBalance(Number(walletData?.balance || 0));
      setGrowthResult(growthData);
      setGrowthLocked(!!assessmentLanding?.inviteOnly);
      setGrowthAllowRetake(assessmentLanding?.allowRetake !== false);
      setPortalSummary(portalData);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'خطا در دریافت اطلاعات داشبورد');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [authLoading, user, navigate, fetchData]);

  useEffect(() => {
    bootstrapProfileForm();
  }, [bootstrapProfileForm]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab && tabs.some((t) => t.key === tab)) {
      setActiveTab(tab as DashboardTabKey);
    }
  }, [location.search]);

  const handleReadMessage = async (id: string) => {
    try {
      await messagesService.markAsRead(id);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, isRead: true } : m)));
    } catch {
      // Keep the UI responsive; a failed mark-as-read is non-blocking.
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileFeedback({});
    try {
      await saveProfile({
        firstName: profileForm.firstName.trim() || null,
        lastName: profileForm.lastName.trim() || null,
        email: profileForm.email.trim() || null,
        phone: profileForm.phone.trim() || null,
        education: profileForm.education.trim() || null,
        university: profileForm.university.trim() || null,
        job: profileForm.job.trim() || null,
        state: profileForm.state.trim() || null,
        gender: profileForm.gender.trim() || null,
      });
      setProfileFeedback({ success: 'اطلاعات پروفایل با موفقیت ذخیره شد.' });
    } catch (err: any) {
      setProfileFeedback({ error: err?.response?.data?.message || 'ذخیره اطلاعات انجام نشد.' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordFeedback({});
    if (passwordForm.newPassword.length < 6) {
      setPasswordFeedback({ error: 'رمز عبور جدید باید حداقل ۶ کاراکتر باشد.' });
      setPasswordSaving(false);
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordFeedback({ error: 'تکرار رمز عبور جدید با مقدار وارد شده یکسان نیست.' });
      setPasswordSaving(false);
      return;
    }
    try {
      await authService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordFeedback({ success: 'رمز عبور با موفقیت تغییر کرد.' });
    } catch (err: any) {
      setPasswordFeedback({ error: err?.response?.data?.message || 'تغییر رمز عبور انجام نشد.' });
    } finally {
      setPasswordSaving(false);
    }
  };

  const renderProfilePanel = () => (
    <div className="grid gap-5 xl:grid-cols-2">
      <DashboardCard>
        <DashboardSectionTitle title="ویرایش اطلاعات کاربری" subtitle="اطلاعات پایه حساب کاربری خود را مدیریت کنید." accent="violet" />
        <form onSubmit={handleProfileSubmit} className="space-y-3">
          {[
            { name: 'firstName', label: 'نام' },
            { name: 'lastName', label: 'نام خانوادگی' },
            { name: 'email', label: 'ایمیل' },
            { name: 'phone', label: 'موبایل' },
            { name: 'education', label: 'تحصیلات' },
            { name: 'university', label: 'دانشگاه' },
            { name: 'job', label: 'شغل' },
            { name: 'state', label: 'استان' },
            { name: 'gender', label: 'جنسیت' },
          ].map((field) => (
            <label key={field.name} className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">{field.label}</span>
              <input
                name={field.name}
                value={(profileForm as any)[field.name]}
                onChange={(event) => setProfileForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-600"
              />
            </label>
          ))}
          {profileFeedback.error ? <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{profileFeedback.error}</p> : null}
          {profileFeedback.success ? <p className="rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{profileFeedback.success}</p> : null}
          <button type="submit" disabled={profileSaving} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
            {profileSaving ? 'در حال ذخیره...' : 'ذخیره اطلاعات'}
          </button>
        </form>
      </DashboardCard>

      <DashboardCard>
        <DashboardSectionTitle title="تغییر رمز عبور" subtitle="برای امنیت بیشتر حساب، رمز عبور خود را به‌روز نگه دارید." accent="violet" />
        <form onSubmit={handlePasswordSubmit} className="space-y-3">
          {[
            { name: 'currentPassword', label: 'رمز عبور فعلی', type: 'password' },
            { name: 'newPassword', label: 'رمز عبور جدید', type: 'password' },
            { name: 'confirmPassword', label: 'تکرار رمز عبور جدید', type: 'password' },
          ].map((field) => (
            <label key={field.name} className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">{field.label}</span>
              <input
                type={field.type}
                name={field.name}
                value={(passwordForm as any)[field.name]}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-600"
              />
            </label>
          ))}
          {passwordFeedback.error ? <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{passwordFeedback.error}</p> : null}
          {passwordFeedback.success ? <p className="rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{passwordFeedback.success}</p> : null}
          <button type="submit" disabled={passwordSaving} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
            {passwordSaving ? 'در حال تغییر...' : 'تغییر رمز عبور'}
          </button>
        </form>
      </DashboardCard>
    </div>
  );

  const panel = (() => {
    switch (activeTab) {
      case 'courses':
        return <DashboardCoursesPanel courses={myCourses} audios={myAudios} />;
      case 'growth':
        return (
          <div className="space-y-8">
          <MyAssessmentHistory key={user?.id} />
          <DashboardGrowthPanel
            result={growthResult}
            locked={growthLocked}
            allowRetake={growthAllowRetake}
          />
          </div>
        );
      case 'portal':
        return <DashboardPortalPanel summary={portalSummary} />;
      case 'workshops':
        return <DashboardWorkshopsPanel workshops={myWorkshops} />;
      case 'videos':
        return <DashboardVideosPanel videos={myVideos} />;
      case 'audios':
        return <DashboardAudiosPanel audios={myAudios} />;
      case 'wallet':
        return <DashboardWalletPanel walletBalance={walletBalance} invoices={invoices} />;
      case 'messages':
        return <DashboardMessagesPanel messages={messages} onMarkRead={handleReadMessage} />;
      case 'orders':
        return <DashboardOrdersPanel invoices={invoices} />;
      case 'profile':
        return renderProfilePanel();
      default:
        return null;
    }
  })();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteBrandedLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-3xl rounded-2xl border border-red-200 bg-red-50 p-5 text-center">
          <p className="font-black text-red-700">{error}</p>
          <button type="button" onClick={fetchData} className="mt-4 rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white">
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      <div className="mx-auto max-w-7xl space-y-5 px-4 pt-6 sm:px-6 lg:px-8">
        <DashboardHero
          userName={user?.firstName || user?.username || 'کاربر عزیز'}
          email={user?.email || undefined}
          walletBalance={walletBalance}
          unreadMessages={unreadMessages}
        />
        <div className="lg:hidden">
          <Link to="/ambassadors" className="mb-4 inline-block rounded-xl bg-teal-700 px-5 py-3 text-white">پنل سفیران من حقیقی</Link>
          <DashboardTabs tabs={tabs} active={activeTab} onChange={(key) => setActiveTab(key as DashboardTabKey)} />
        </div>
        <div className="grid gap-5 lg:grid-cols-[290px_1fr]">
          <aside className="hidden lg:block">
            <Link to="/ambassadors" className="mb-4 block rounded-xl bg-teal-700 px-5 py-3 text-center text-white">پنل سفیران من حقیقی</Link>
            <DashboardSidebar items={sidebarItems} active={activeTab} onChange={(key) => setActiveTab(key as DashboardTabKey)} />
          </aside>
          <main className="min-w-0">{panel}</main>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
